'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:recommendations');
const { personalizedSlice } = require('../../lib/util.js');
const RECOMMENDATIONS_READY = 'phoenix:recommendations:ready';
const RECOMMENDATIONS_USE_BASE = 'phoenix:recommendations:use_base';

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * PhoenixRecommendations is a Web Component responsible for retrieving recommended
     * content via LiftIgniter's API based on the parameters passed in. It is also responsible for
     * passing in content ids to PhoenixCards
     * PhoenixRecommendations relies on the server-side LiftIgniter module, which defines and
     * uses $p to send beacons per pageview.
     *
     * Documentation: https://liftigniter.readme.io/docs/introduction
     *
     * @example
     *
     *     <phoenix-recommendations
     *          ...
     *         config={
     *                 "isInContentRecommendation": false,
     *                 "siteDomain": "fashionista.com",
     *                 "size":9,
     *                 "widgetName":"This-is-widget-name",}
     *         percentage_for_personalized_recommendation=50
     *         key_values_for_personalized_recommendation={
     *              "section": "Fantasy",
     *              "site_name": "HubPages",
     *              "published_time": 1607031471
     *              ...
     *          }
     *          additional_personalized_slices=[]
     *     </phoenix-recommendations>
     */
    class PhoenixRecommendations extends pageClass.PhoenixElement {
        set cardRenderedPromises(val) {
            if (val) {
                if (!this._promises) {
                    this._promises = [];
                }
                this._promises.push(val);
            }
        }

        get cardRenderedPromises() {
            return this._promises;
        }

        set data(val = []) {
            this._data = val;
        }

        get data() {
            return this._data;
        }

        get isInContentRecommendation() {
            return this.config.isInContentRecommendation;
        }

        set loaded(val) {
            if (val) {
                this._isLoaded = val;
            }
        }

        get loaded() {
            return this._isLoaded;
        }

        get widgetName() {
            return this.config.widgetName;
        }

        set useBaseCardOnConnected(val) {
            if (val) {
                this._useBaseCardOnConnected = val;
            }
        }

        get useBaseCardOnConnected() {
            return this._useBaseCardOnConnected;
        }

        set recordedTimesForFetching(val) {
            if (!window._recommendedContentRecordedTimesForFetching) {
                window._recommendedContentRecordedTimesForFetching = {};
            }

            if (val) {
                window._recommendedContentRecordedTimesForFetching = {
                    ...window._recommendedContentRecordedTimesForFetching,
                    ...val,
                };
            }
        }

        get recordedTimesForFetching() {
            return window._recommendedContentRecordedTimesForFetching;
        }

        set recordedTimes(val) {
            if (!this._recordedTimes) {
                this._recordedTimes = {};
            }

            if (val) {
                this._recordedTimes = {
                    ...this._recordedTimes,
                    ...val,
                };
            }
        }

        get percentageForPersonalizedRecommendation() {
            return parseInt(
                this.getAttr('percentage_for_personalized_recommendation', {
                    required: true,
                })
            );
        }

        get keyValuesForPersonalizedRecommendation() {
            try {
                const keyValuesRaw = this.getAttr('key_values_for_personalized_recommendation', {
                    required: false,
                });
                if (keyValuesRaw) {
                    return JSON.parse(keyValuesRaw);
                } else {
                    return {};
                }
            } catch (e) {
                return {};
            }
        }

        get additionalPersonalizedSlices() {
            try {
                const additionalPersonalizedSlicesRaw = this.getAttr('additional_personalized_slices', {
                    required: false,
                });
                if (additionalPersonalizedSlicesRaw) {
                    return JSON.parse(additionalPersonalizedSlicesRaw);
                } else {
                    return [];
                }
            } catch (e) {
                return [];
            }
        }

        get recordedTimes() {
            return this._recordedTimes;
        }

        constructor() {
            super();
            window.recommendedContentRequest = window.recommendedContentRequest || {
                widgetPromises: [],
                userHashPromises: [],
            };
            window.recommendedContentRequest.widgetPromises.push(
                new Promise((resolve) => {
                    this.contentPromiseResolve = resolve;
                })
            );
        }

        connectedCallback() {
            super.connectedCallback();
            if (
                navigator.connection &&
                navigator.connection.effectiveType &&
                (navigator.connection.effectiveType === 'slow-2g' ||
                    navigator.connection.effectiveType === '2g' ||
                    navigator.connection.effectiveType === '3g')
            ) {
                // in case connectedCallback of child cards have not been called until now
                this.useBaseCardOnConnected = true;
                // in case connectedCallback of child cards have been called by now
                this.showBaseOrHide();
                this.trackRecommendations('base', 'user-slow-connection');
            } else if (navigator.connection && navigator.connection.saveData) {
                // in case connectedCallback of child cards have not been called until now
                this.useBaseCardOnConnected = true;
                // in case connectedCallback of child cards have been called by now
                this.showBaseOrHide();
                this.trackRecommendations('base', 'user-saving-data');
            } else {
                const personalizedSlices = [
                    {
                        percentageForSlice: this.percentageForPersonalizedRecommendation,
                        keyValuesForSlice: this.keyValuesForPersonalizedRecommendation,
                    },
                ].concat(this.additionalPersonalizedSlices || []);
                const getMavenUidQueryablePromise = this.makeQueryablePromise(
                    window.GdprStatus.getMavenUid()
                );
                Promise.all([getMavenUidQueryablePromise, window.liftIgniterPromise])
                    .then((values) => {
                        const timeoutPromise = new Promise((resolve, reject) => {
                            setTimeout((values) => {
                                reject('li beacon failed to load in 5 seconds.');
                            }, 5000);
                        });

                        Promise.race([window.liftIgniterLoadPromise, timeoutPromise])
                            .then(() => {
                                this.mavenUserId = values[0];
                                window.$p('userHash', (hash) => {
                                    if (!this.loaded) {
                                        this.loaded = true;
                                        const effectiveSlice = personalizedSlice(hash, personalizedSlices);
                                        if (effectiveSlice) {
                                            this.userHashPromiseResolve && this.userHashPromiseResolve(true);
                                            Promise.all(
                                                window.recommendedContentRequest.userHashPromises
                                            ).then(() => {
                                                this.requestRecommendedItems(
                                                    effectiveSlice.keyValuesForSlice
                                                );
                                            });
                                            if (!window.recommendedContentRequest.initFetch) {
                                                window.recommendedContentRequest.initFetch = Promise.all(
                                                    window.recommendedContentRequest.widgetPromises
                                                ).then(() => {
                                                    delete window.recommendedContentRequest;
                                                    this.recordedTimesForFetching = {
                                                        liModelFetchStartTime: performance.now(),
                                                    };
                                                    window.$p('fetch');
                                                });
                                            }
                                        } else {
                                            this.showBaseOrHide();
                                            this.trackRecommendations('base', 'base-user-hash');
                                            this.userHashPromiseResolve && this.userHashPromiseResolve(true);
                                            this.contentPromiseResolve && this.contentPromiseResolve(true);
                                        }
                                    }
                                });
                            })
                            .catch((err) => {
                                if (!this.loaded) {
                                    // eslint-disable-next-line no-console
                                    console.error(err);
                                    this.loaded = true;
                                    this.showBaseOrHide();
                                    this.trackRecommendations('base', 'li-load-promise-erred');
                                }
                            });
                    })
                    .catch((err) => {
                        const reasonForFallbackToBase = getMavenUidQueryablePromise.isRejected()
                            ? 'maven-uid-promise-erred'
                            : 'li-promise-erred';
                        this.showBaseOrHide();
                        this.trackRecommendations('base', reasonForFallbackToBase);
                        // eslint-disable-next-line no-console
                        console.error(err && err.message ? err.message : err);
                    });
            }
        }

        requestRecommendedItems(keyValues) {
            const baseUrl = 'https://' + this.config.siteDomain;
            // liftIgniterPromise is created in the server-side
            // and we expect the promise to resolve when that's done.
            window.$p('setRequestFields', ['tempestId', 'channelKey']);
            window.$p('register', {
                max: this.config.size,
                widget: this.widgetName,
                opts: {
                    f: ['tempestId', 'channelKey'],
                    mf: ['tempestId', 'channelKey'],
                    excludeItems: window.nonPersonalizedItems
                        ? window.nonPersonalizedItems.map((i) => (i.startsWith('/') ? `${baseUrl}${i}` : i))
                        : [],
                    objectType: 'Article',
                    ...keyValues,
                },
                callback: (resp) => {
                    this.recordedTimesForFetching = {
                        liModelFetchEndTime: performance.now(),
                    };

                    const items = resp && resp.items;
                    if (items && items.length >= this.config.size) {
                        this.data = items
                            .slice(0, this.config.size)
                            .map((item) => ({ ...item, siteBasePath: this.config.siteBasePath }));
                        this.notify(RECOMMENDATIONS_READY, this.widgetName);

                        if (
                            this.cardRenderedPromises &&
                            this.cardRenderedPromises.length === this.config.size
                        ) {
                            Promise.all(this.cardRenderedPromises)
                                .then((results) => {
                                    this.recordedTimes = {
                                        allCardsRenderEndTime: performance.now(),
                                    };
                                    if (results.indexOf(true) > -1) {
                                        this.trackRecommendations('LI');
                                    } else {
                                        this.trackRecommendations(
                                            'base',
                                            'all-card-render-promises-returned-false'
                                        );
                                    }
                                })
                                .catch((err) => {
                                    this.showBaseOrHide();
                                    this.trackRecommendations(
                                        'base',
                                        'all-card-rendered-promise-error:' + err.message
                                    );
                                    // eslint-disable-next-line no-console
                                    console.error(err && err.message ? err.message : err);
                                });
                        } else {
                            this.showBaseOrHide();
                            this.trackRecommendations(
                                'base',
                                'num-cards-to-be-rendered-less-than-config-size'
                            );
                        }
                    } else {
                        this.showBaseOrHide();
                        this.trackRecommendations('base', 'num-li-model-items-less-than-config-size');
                    }
                },
                onTimeout: () => {
                    this.showBaseOrHide();
                    this.trackRecommendations('base', 'li-fetch-timeout');
                },
            });
            // An existence check here avoids spurious errors reported to LI back-end on os: Linux x86_64
            // which we don't officially support
            this.contentPromiseResolve && this.contentPromiseResolve(true);
        }

        notify(eventName, eventValue) {
            var customEventInit = {
                bubbles: true,
            };

            if (eventValue) {
                customEventInit.detail = {
                    key: eventName,
                    value: eventValue,
                };

                this.emitEvent(eventName, customEventInit);
            }
        }

        trackRecommendations(slice, reasonForFallbackToBase = null) {
            const recommendedUnitSelector = 'phoenix-super-link';
            var opts = {
                fallbackToBase: reasonForFallbackToBase ? reasonForFallbackToBase : 'null',
                liModelFetchStartTime:
                    this.recordedTimesForFetching && this.recordedTimesForFetching.liModelFetchStartTime
                        ? Math.trunc(this.recordedTimesForFetching.liModelFetchStartTime)
                        : undefined,
                liModelFetchEndTime:
                    this.recordedTimesForFetching && this.recordedTimesForFetching.liModelFetchEndTime
                        ? Math.trunc(this.recordedTimesForFetching.liModelFetchEndTime)
                        : undefined,
                allCardsRenderTimeTaken:
                    this.recordedTimes &&
                    this.recordedTimes.allCardsRenderEndTime &&
                    this.recordedTimesForFetching.liModelFetchEndTime
                        ? Math.trunc(
                              this.recordedTimes.allCardsRenderEndTime -
                                  this.recordedTimesForFetching.liModelFetchEndTime
                          )
                        : undefined,
                cardRenderTimes:
                    this.recordedTimes && this.recordedTimes.cardRenderTimes
                        ? Object.values(this.recordedTimes.cardRenderTimes)
                        : undefined,
            };
            opts = JSON.parse(JSON.stringify(opts));
            window.liftIgniterPromise.then(() => {
                window.$p('track', {
                    elements: this.querySelectorAll(recommendedUnitSelector),
                    name: this.widgetName,
                    source: slice,
                    noTag: true,
                    opts,
                });
            });
        }

        showBaseOrHide() {
            if (this.isInContentRecommendation) {
                this.hide();
            } else {
                this.notify(RECOMMENDATIONS_USE_BASE, this.widgetName);
            }
        }

        hide() {
            var section = this.closest('.m-in-content-recommendation');
            if (section) {
                section.hidden = true;
            }
        }
    }

    window.customElements.define('phoenix-recommendations', PhoenixRecommendations);
    measure.end();
});
